
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Mail, 
  Users, 
  BarChart, 
  Upload, 
  Brain, 
  Target, 
  Clock, 
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Zap,
  Database,
  MessageSquare
} from "lucide-react";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navigation */}
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold text-gray-900">ScoutLink.ai</div>
          <div className="space-x-4">
            <Link to="/request-access">
              <Button variant="ghost">Request Access</Button>
            </Link>
            <Link to="/analytics">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/request-access">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <Badge className="mb-6 bg-blue-100 text-blue-800">AI-Powered Fundraising</Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Save <span className="text-blue-600">100+ Hours</span> on VC Research & Outreach
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            AI-powered platform that finds, analyzes, and connects you with the right VCs. 
            Transform 6 months of fundraising prep into 6 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-access">
              <Button size="lg" className="text-lg px-8 py-4">
                Start Your Fundraising Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/request-access">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4">
                View Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          <div className="space-y-2">
            <div className="text-4xl font-bold text-blue-600">500+</div>
            <div className="text-gray-600">VCs Processed in Minutes</div>
          </div>
          <div className="space-y-2">
            <div className="text-4xl font-bold text-green-600">100+</div>
            <div className="text-gray-600">Hours Saved on Research</div>
          </div>
          <div className="space-y-2">
            <div className="text-4xl font-bold text-purple-600">10x</div>
            <div className="text-gray-600">Faster Email Generation</div>
          </div>
          <div className="space-y-2">
            <div className="text-4xl font-bold text-orange-600">3</div>
            <div className="text-gray-600">Integrated Workspaces</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to Close Your Round
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From data management to personalized outreach, ScoutLink.ai automates your entire VC research and communication workflow.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Smart Data Management */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Database className="h-12 w-12 text-blue-600 mb-4" />
              <CardTitle>Smart Data Management</CardTitle>
              <CardDescription>
                Upload and organize your investor lists with automatic contact matching and real-time sync.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Excel/CSV upload support
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Automatic contact matching
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Batch processing capabilities
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* AI-Powered Analysis */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Brain className="h-12 w-12 text-purple-600 mb-4" />
              <CardTitle>AI-Powered Analysis</CardTitle>
              <CardDescription>
                Advanced AI analyzes investor fit and scores relevance to your startup.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  0-10 relevance scoring with color zones
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Investment thesis matching
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Automated enrichment via Perplexity
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Personalized Emails */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <MessageSquare className="h-12 w-12 text-green-600 mb-4" />
              <CardTitle>AI Email Generation</CardTitle>
              <CardDescription>
                Generate personalized introduction emails that get responses.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  OpenAI-powered personalization
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Business context integration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Draft management system
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Analytics Dashboard */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <BarChart className="h-12 w-12 text-orange-600 mb-4" />
              <CardTitle>Advanced Analytics</CardTitle>
              <CardDescription>
                Track your outreach performance and optimize your fundraising strategy.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Response rate monitoring
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Pipeline visualization
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  ROI tracking dashboard
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Time Savings */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Clock className="h-12 w-12 text-red-600 mb-4" />
              <CardTitle>Massive Time Savings</CardTitle>
              <CardDescription>
                Automate 80% of your research work and focus on what matters most.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  80+ hours saved on research
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Bulk processing (50-100 VCs)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Smart prioritization
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Integration Power */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Zap className="h-12 w-12 text-yellow-600 mb-4" />
              <CardTitle>Powerful Integrations</CardTitle>
              <CardDescription>
                Connect with the best AI tools and email providers for maximum efficiency.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  OpenAI & Perplexity integration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Gmail & Outlook support
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Real-time data sync
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-6 py-20 bg-white/50 backdrop-blur">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            From Data to Deals in 3 Simple Steps
          </h2>
          <p className="text-xl text-gray-600">
            Our streamlined workflow gets you from investor lists to personalized outreach in minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <Upload className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-4">1. Upload Your Data</h3>
            <p className="text-gray-600 mb-4">
              Import your VC lists via Excel/CSV or manual entry. Our system automatically organizes and validates the data.
            </p>
            <Link to="/data">
              <Button variant="outline">Data Workspace</Button>
            </Link>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold mb-4">2. AI Analysis & Scoring</h3>
            <p className="text-gray-600 mb-4">
              Our AI analyzes each investor's fit with your startup and provides relevance scores with actionable insights.
            </p>
            <Link to="/analytics">
              <Button variant="outline">Analytics Hub</Button>
            </Link>
          </div>

          <div className="text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-4">3. Generate & Send Emails</h3>
            <p className="text-gray-600 mb-4">
              Create personalized introduction emails with AI assistance and track responses through your preferred email provider.
            </p>
            <Link to="/email">
              <Button variant="outline">Email Workspace</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Transform Your Fundraising?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join startups who've saved hundreds of hours and closed faster with ScoutLink.ai
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-access">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/request-access">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 text-white border-white hover:bg-white hover:text-gray-900">
                View Live Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-8 border-t">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="text-gray-600 mb-4 md:mb-0">
            © 2024 ScoutLink.ai. Empowering startups to find the right investors.
          </div>
          <div className="flex space-x-6">
            <Link to="/data" className="text-gray-600 hover:text-gray-900">Data Workspace</Link>
            <Link to="/analytics" className="text-gray-600 hover:text-gray-900">Analytics</Link>
            <Link to="/email" className="text-gray-600 hover:text-gray-900">Email Tools</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
